export type WebLogLevel = 'info' | 'warn' | 'error';

export interface WebLogAttributes {
  url?: string;
  name?: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  [key: string]: unknown;
}

export interface WebLogPayload {
  level: WebLogLevel;
  message: string;
  attributes?: WebLogAttributes;
}

/**
 * Strip query string and hash from a URL so credential tokens carried in query
 * params (auth flows on this app use ?token=…) never reach the remote log store.
 */
function sanitizeUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

// Sliding-window rate limiter: at most MAX_POSTS_PER_WINDOW POSTs per WINDOW_MS.
const WINDOW_MS = 10_000;
const MAX_POSTS_PER_WINDOW = 30;
const recentTimestamps: number[] = [];

/**
 * Fire-and-forget POST to the auth-api client-log endpoint.
 * Skipped entirely in local development (REACT_APP_ENV === 'development').
 * Rate-limited to MAX_POSTS_PER_WINDOW calls per WINDOW_MS to prevent error/render
 * loops from flooding the server.
 */
export function webLogPost({ level, message, attributes }: WebLogPayload): void {
  if (process.env.REACT_APP_ENV === 'development') {
    return;
  }

  // Read process.env directly (not getAuthApiUrl from app-config) to avoid a circular import.
  const rawHost = (process.env.REACT_APP_MAIN_URL ?? '').trim();
  if (!rawHost) {
    return;
  }

  // Sliding-window gate: drop stale timestamps, then check the count.
  const now = Date.now();
  const cutoff = now - WINDOW_MS;
  while (recentTimestamps.length > 0 && (recentTimestamps[0] as number) <= cutoff) {
    recentTimestamps.shift();
  }
  if (recentTimestamps.length >= MAX_POSTS_PER_WINDOW) {
    return;
  }
  recentTimestamps.push(now);

  const host = rawHost.replace(/\/$/, '');
  const url = `${host}/api/v1/public/client-log`;
  const body = {
    attributes: {
      // Caller attributes spread first so the captured url always wins.
      ...attributes,
      url: sanitizeUrl(window.location.href),
    },
    level,
    message,
    service: 'pp-gateway',
  };

  void fetch(url, {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    method: 'POST',
  }).catch(() => {
    // swallow — fire-and-forget
  });
}
